import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from '../../common.js';
/**
 * Push module (ROADMAP §1.5, WS-3). Not a collaborative Yjs module — no doc name — just a
 * REST + web-push utility that other modules (calendar, lists, ...) will call into via
 * `sendToAll()` once they own reminder/notification logic.
 */
/** The two keys a browser's PushManager subscription carries (RFC 8291). */
export const pushSubscriptionKeysSchema = z.object({
    p256dh: z.string().min(1),
    auth: z.string().min(1),
});
/** Shape of `PushSubscription.toJSON()` in the browser. */
export const pushSubscriptionJsonSchema = z.object({
    endpoint: z.string().url(),
    expirationTime: z.number().nullable().optional(),
    keys: pushSubscriptionKeysSchema,
});
/** Body for `POST /api/push/subscribe`. */
export const subscribeRequestSchema = z.object({
    subscription: pushSubscriptionJsonSchema,
    /**
     * Ignored by the server: the owner is derived from the session cookie (see
     * `apps/server/src/modules/push/routes.ts`). Kept optional-and-accepted so an older
     * installed client that still sends it doesn't get a 400.
     */
    userId: idSchema.optional(),
});
/** Body for `DELETE /api/push/subscribe`. */
export const unsubscribeRequestSchema = z.object({
    endpoint: z.string().url(),
});
/** A stored subscription row (`push_subscriptions` table, `migrations/push/`). */
export const pushSubscriptionRecordSchema = z.object({
    id: idSchema,
    endpoint: z.string().url(),
    keys: pushSubscriptionKeysSchema,
    userId: idSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
/** Notification payload shape sent through web-push; the SW reads this in `push` events. */
export const pushPayloadSchema = z.object({
    title: z.string().min(1).max(200),
    body: z.string().max(1000),
    /** Path (not full URL) to focus/open on notificationclick, e.g. `/lists/abc`. */
    url: z.string().max(500).default('/'),
    tag: z.string().max(100).optional(),
    icon: z.string().max(500).optional(),
});
/** POST /api/push/poke — an authed member pings their own devices or everyone else's.
 * A human pressing a button, never a machine loop, so it lives outside the
 * no-demand-mechanics concerns (memory/no-demand-mechanics.md). */
export const pokeRequestSchema = z.object({
    /** 'me' = my own devices (the test button); 'others' = every other member (the poke box). */
    to: z.enum(['me', 'others']),
    message: z.string().trim().min(1).max(120).optional(),
});
//# sourceMappingURL=index.js.map